import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useSpring } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import Seo from "@/components/Seo";
import Reveal from "@/components/Reveal";
import KineticLines from "@/components/KineticLines";
import CTABand from "@/components/CTABand";
import { METODO_ETAPAS } from "@/lib/constants";

const ComoTrabalhamos = () => {
    const timelineRef = useRef(null);
    const { scrollYProgress } = useScroll({ target: timelineRef, offset: ["start 0.75", "end 0.6"] });
    const progress = useSpring(scrollYProgress, { stiffness: 60, damping: 20 });

    return (
        <main>
            <Seo
                title="Como Trabalhamos — Método Vorantis em 4 etapas"
                description="Entender, diagnosticar, planejar, executar e acompanhar: o método da Vorantis para transformar análise em evolução concreta para sua empresa."
            />

            <section className="grain-overlay hero-grid-bg relative overflow-hidden bg-navy-deep pb-24 pt-40 md:pb-32 md:pt-48" data-testid="metodo-hero">
                <div className="pointer-events-none absolute -right-40 top-1/3 h-[420px] w-[420px] rounded-full bg-emerald-500/15 blur-[140px]" />
                <div className="relative mx-auto max-w-7xl px-6 md:px-10">
                    <motion.div
                        className="flex items-center gap-3"
                        initial={{ opacity: 0, y: 16 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.6, delay: 0.15 }}
                    >
                        <span className="h-px w-10 bg-emerald-400" />
                        <span className="font-mono text-[11px] font-bold uppercase tracking-[0.3em] text-emerald-400">Método de trabalho</span>
                    </motion.div>
                    <h1 className="mt-8 max-w-3xl font-display text-4xl font-extrabold leading-[1.04] tracking-tight text-white sm:text-6xl">
                        <KineticLines
                            testid="metodo-headline"
                            delay={0.3}
                            lines={["Da análise", <>à <span className="text-emerald-400">execução.</span></>]}
                        />
                    </h1>
                    <motion.p
                        className="mt-7 max-w-2xl text-base leading-relaxed text-slate-300 sm:text-lg"
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.7, delay: 0.85 }}
                    >
                        Um método em quatro movimentos, construído a partir da realidade da sua
                        empresa — sem fórmulas prontas.
                    </motion.p>
                </div>
            </section>

            <section className="bg-slate-50 py-24 md:py-32" data-testid="timeline-section">
                <div className="mx-auto max-w-5xl px-6 md:px-10">
                    <div ref={timelineRef} className="relative">
                        <div className="absolute bottom-6 left-[27px] top-6 w-px bg-slate-200 md:left-1/2" aria-hidden="true" />
                        <motion.div
                            className="absolute bottom-6 left-[27px] top-6 w-px bg-emerald-500 md:left-1/2"
                            style={{ scaleY: progress, originY: 0 }}
                            aria-hidden="true"
                        />
                        <div className="flex flex-col gap-16 md:gap-24">
                            {METODO_ETAPAS.map((etapa, i) => (
                                <div
                                    key={etapa.numero}
                                    data-testid={`timeline-step-${i + 1}`}
                                    className={`relative grid items-center gap-6 pl-20 md:grid-cols-2 md:gap-16 md:pl-0 ${
                                        i % 2 === 0 ? "" : ""
                                    }`}
                                >
                                    <div className="absolute left-0 top-1 flex h-14 w-14 items-center justify-center rounded-full border border-emerald-500/40 bg-navy-deep font-mono text-sm font-bold text-emerald-400 md:relative md:left-auto md:top-auto md:justify-self-end">
                                        {etapa.numero}
                                    </div>
                                    <Reveal className={i % 2 === 0 ? "md:order-2" : "md:order-1"}>
                                        <div className={`rounded-3xl border border-slate-200 bg-white p-8 shadow-[0_20px_48px_-32px_rgba(10,25,47,0.3)] md:p-10 ${i % 2 === 0 ? "" : "md:text-right"}`}>
                                            <h2 className="font-display text-2xl font-extrabold tracking-tight text-slate-900 md:text-3xl">
                                                {etapa.titulo}
                                            </h2>
                                            <div className={`mt-4 h-1 w-12 rounded-full bg-emerald-500 ${i % 2 === 0 ? "" : "md:ml-auto"}`} />
                                            <p className="mt-5 text-sm leading-relaxed text-slate-600 md:text-base">
                                                {etapa.descricao}
                                            </p>
                                        </div>
                                    </Reveal>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </section>

            <section className="grain-overlay bg-navy-deep py-24 md:py-28" data-testid="metodo-compromisso-section">
                <div className="mx-auto max-w-7xl px-6 text-center md:px-10">
                    <Reveal>
                        <p className="font-mono text-[11px] font-bold uppercase tracking-[0.3em] text-emerald-400">Nosso compromisso</p>
                        <h2 className="mx-auto mt-6 max-w-3xl font-display text-3xl font-extrabold tracking-tight text-white sm:text-4xl">
                            Implementamos as soluções e acompanhamos os ajustes — lado a lado com a sua equipe.
                        </h2>
                        <Link
                            to="/contato"
                            data-testid="metodo-cta-diagnostico"
                            className="mt-10 inline-flex items-center gap-2 rounded-full bg-emerald-500 px-8 py-4 font-semibold text-white transition-colors hover:bg-emerald-600"
                        >
                            Iniciar com um diagnóstico
                            <ArrowUpRight size={18} />
                        </Link>
                    </Reveal>
                </div>
            </section>

            <CTABand />
        </main>
    );
};

export default ComoTrabalhamos;
